class SharkRewardsSection extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.shark-rewards__card'));
    if (this.cards.length === 0) return;

    this._onBreakpointChange = this.onBreakpointChange.bind(this);
    this.mobileQuery = window.matchMedia('(max-width: 990px)');
    this.mobileQuery.addEventListener('change', this._onBreakpointChange);

    requestAnimationFrame(() => this.onBreakpointChange());
  }

  onBreakpointChange() {
    if (this.mobileQuery.matches) {
      this.destroyDesktop();
    } else {
      this.initDesktop();
    }
  }

  initDesktop() {
    if (this._scrollHandler) return;

    const carousel = this.querySelector('.shark-rewards__carousel');
    if (!carousel) return;

    this._carousel = carousel;
    carousel.style.overflowY = 'hidden';

    this._scrollHandler = () => this._onPageScroll();
    window.addEventListener('scroll', this._scrollHandler, { passive: true });

    this._wheelHandler = (e) => this._onWheel(e);
    this._keyHandler   = (e) => this._onKey(e);
    window.addEventListener('wheel', this._wheelHandler, { passive: false });
    window.addEventListener('keydown', this._keyHandler);

    // Custom scrollbar
    this._thumb = this.querySelector('.shark-rewards__scrollbar-thumb');
    this._track = this.querySelector('.shark-rewards__scrollbar-track');

    // Active card fades via IntersectionObserver
    this._cardObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => e.target.classList.toggle('is-active', e.isIntersecting));
    }, { root: carousel, threshold: 0.5 });
    this.cards.forEach(c => this._cardObserver.observe(c));

    this._locked       = false;
    this._passedDown   = false; // true after forward-disengage; blocks re-engagement going down
    this._lastScrollY  = window.scrollY;
  }

  // ── Engage: lock page scroll ─────────────────────────────────────────────────

  _engage(from = 'top') {
    if (this._locked) return;
    this._locked = true;

    this._lockedScrollY = Math.round(this.getBoundingClientRect().top + window.scrollY);
    window.scrollTo(0, this._lockedScrollY);
    document.documentElement.style.overflow = 'hidden';

    const carousel = this._carousel;
    if (from === 'bottom') {
      // Start at the last card when entering from below
      carousel.scrollTop = carousel.scrollHeight - carousel.clientHeight;
      if (this._thumb && this._track) this.updateScrollbar(carousel);
    } else {
      carousel.scrollTop = 0;
    }
  }

  // ── Disengage: restore page scroll ──────────────────────────────────────────

  _disengage(direction = 'backward') {
    if (!this._locked) return;
    this._locked = false;
    document.documentElement.style.overflow = '';

    if (direction === 'forward') {
      // Let natural page scroll continue past the section.
      // Set flag so we don't re-lock until the section is fully behind the user
      // or they've scrolled back above it.
      this._passedDown = true;
      this._lastScrollY = window.scrollY;
    } else {
      // Backward: snap just above the section top so scrolling up is natural
      const above = Math.max(0, this._lockedScrollY - 1);
      window.scrollTo(0, above);
      this._lastScrollY = above;
    }
  }

  // ── Page scroll: engage on entry from top or bottom ─────────────────────────

  _onPageScroll() {
    const scrollingDown = window.scrollY > this._lastScrollY;
    this._lastScrollY = window.scrollY;

    if (this._locked) return;

    const rect = this.getBoundingClientRect();

    // Reset passedDown once the section is fully above the viewport (scrolled past),
    // or once the user has scrolled back above it (section below viewport again).
    if (this._passedDown && (rect.bottom <= 0 || rect.top > 50)) {
      this._passedDown = false;
    }

    if (scrollingDown && !this._passedDown && rect.top <= 0 && rect.bottom > 0) {
      // Section top crossed viewport top while scrolling down
      this._engage('top');
    } else if (!scrollingDown && !this._passedDown && rect.top > -80 && rect.top <= 0 && rect.bottom > 0) {
      // Section top is within 80px above viewport top while scrolling up — small snap, engage from bottom
      this._engage('bottom');
    }
  }

  // ── Wheel: advance/retreat carousel while locked ─────────────────────────────

  _onWheel(e) {
    if (!this._locked) return;
    e.preventDefault();

    const carousel = this._carousel;
    const max = carousel.scrollHeight - carousel.clientHeight;

    if (e.deltaY > 0 && carousel.scrollTop >= max - 1) {
      // Carousel at end — release, let page scroll continue past section
      this._disengage('forward');
      return;
    }
    if (e.deltaY < 0 && carousel.scrollTop <= 0) {
      // Carousel at start — release, scroll back above section
      this._disengage('backward');
      return;
    }

    if (this._advancing) return; // debounce rapid wheel events

    this._advancing = true;
    clearTimeout(this._advanceTimer);
    this._advanceTimer = setTimeout(() => { this._advancing = false; }, 650);

    const cardHeight = carousel.clientHeight;
    const direction  = e.deltaY > 0 ? 1 : -1;
    const current    = Math.round(carousel.scrollTop / cardHeight);
    const next       = Math.max(0, Math.min(this.cards.length - 1, current + direction));

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    carousel.scrollTo({ top: next * cardHeight, behavior: reducedMotion ? 'instant' : 'smooth' });

    if (this._thumb && this._track) {
      setTimeout(() => this.updateScrollbar(carousel), 350);
    }
  }

  // ── Keyboard: Space/ArrowDown advance, ArrowUp retreat, Escape exit ─────────

  _onKey(e) {
    if (!this._locked) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this._disengage('forward');
      return;
    }

    const down = e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ';
    const up   = e.key === 'ArrowUp'   || e.key === 'PageUp';
    if (!down && !up) return;

    e.preventDefault();
    this._onWheel({ deltaY: down ? 1 : -1, preventDefault: () => {} });
  }

  // ── Scrollbar ────────────────────────────────────────────────────────────────

  updateScrollbar(carousel) {
    const { scrollTop, scrollHeight, clientHeight } = carousel;
    const trackHeight = this._track.offsetHeight;
    const thumbHeight = 30;
    const maxThumbTop = trackHeight - thumbHeight;
    const thumbTop = scrollHeight > clientHeight
      ? (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop
      : 0;
    this._thumb.style.height = thumbHeight + 'px';
    this._thumb.style.top    = thumbTop + 'px';
  }

  // ── Cleanup ──────────────────────────────────────────────────────────────────

  destroyDesktop() {
    if (this._locked) this._disengage();

    this._passedDown = false;

    window.removeEventListener('scroll',  this._scrollHandler);
    window.removeEventListener('wheel',   this._wheelHandler);
    window.removeEventListener('keydown', this._keyHandler);
    this._scrollHandler = null;
    this._wheelHandler  = null;
    this._keyHandler    = null;

    if (this._cardObserver) {
      this._cardObserver.disconnect();
      this._cardObserver = null;
    }

    clearTimeout(this._advanceTimer);

    if (this._carousel) {
      this._carousel.style.overflowY = '';
      this._carousel.scrollTop = 0;
      this._carousel = null;
    }

    this.cards.forEach(c => c.classList.remove('is-active'));
    this._thumb = null;
    this._track = null;
  }

  disconnectedCallback() {
    this.destroyDesktop();
    if (this.mobileQuery) {
      this.mobileQuery.removeEventListener('change', this._onBreakpointChange);
    }
  }
}

customElements.define('shark-rewards-section', SharkRewardsSection);
